import React, { useEffect, useState } from 'react';
import { Check, CheckCheck, Send } from 'lucide-react';
import type { TFunction } from 'i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { conversationsApi } from '../api/conversationsApi';
import { splitReceipts, type MessageReceipts, type ParticipantReceipt } from '../readState';

/** Local time for a server timestamp, matching the bubble's own format. */
function timeOf(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * One row of the lifecycle. Rendered only for a state that has actually
 * happened — a message nobody has opened shows no Read row at all, rather than
 * a Read row with a dash or, worse, an invented time.
 */
const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  time: string;
  testId: string;
}> = ({ icon, label, time, testId }) => (
  <div className="flex items-center justify-between py-2" data-testid={testId}>
    <span className="flex items-center gap-2 text-sm text-foreground">
      {icon}
      {label}
    </span>
    <span className="text-sm tabular-nums text-muted-foreground">{time}</span>
  </div>
);

/**
 * One labelled group of people and when they got there.
 *
 * The heading carries the authoritative count and is always shown; the list
 * under it is only what the server sent. Above the participant cap there is no
 * list at all, and the heading is then the whole answer — which is why the
 * count never comes from the array's length.
 */
const PersonGroup: React.FC<{
  testId: string;
  heading: string;
  people: ParticipantReceipt[];
  timeKey: 'read_at' | 'delivered_at';
  nameOf?: (userId?: string) => string;
}> = ({ testId, heading, people, timeKey, nameOf }) => (
  <div data-testid={testId}>
    <p className="text-sm font-medium text-foreground">{heading}</p>
    {people.length > 0 && (
      <ul className="mt-1 space-y-0.5">
        {people.map((p) => (
          <li key={p.user_id} className="flex items-center justify-between text-sm">
            <span className="truncate text-muted-foreground">
              {nameOf ? nameOf(p.user_id) : p.user_id}
            </span>
            <span className="ml-3 shrink-0 tabular-nums text-muted-foreground">
              {timeOf(p[timeKey])}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

/**
 * Message Info for one of the sender's own messages.
 *
 * Every timestamp here is the SERVER's, fetched on open — never derived from
 * local state. The whole point of this panel is that it is authoritative, so
 * showing a locally-guessed time would defeat it.
 */
export const MessageInfoDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  messageId: string;
  /** The message's own created_at — the Sent time, which is always known. */
  sentAt: string;
  /** Resolves a user_id to a display name. Receipts come back as ids only —
   *  the server has no business shipping a name roster for this. */
  nameOf?: (userId?: string) => string;
  t: TFunction;
}> = ({ open, onOpenChange, conversationId, messageId, sentAt, nameOf, t }) => {
  const [data, setData] = useState<MessageReceipts | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    if (!open) return;
    let stopped = false;
    setState('loading');
    setData(null);
    conversationsApi
      .messageReceipts(conversationId, messageId)
      .then((r) => {
        if (stopped) return;
        setData(r);
        setState('ready');
      })
      .catch(() => {
        // Say so rather than rendering an empty panel, which would read as
        // "nobody has seen this".
        if (!stopped) setState('error');
      });
    return () => {
      stopped = true;
    };
  }, [open, conversationId, messageId]);

  // One-to-one: a single recipient, so the panel is the plain lifecycle. The
  // group breakdown lands in the next slice; the counts below already come
  // from the same response.
  const direct = !!data && data.recipients === 1;
  const only = data?.detail?.[0];
  const readTime = timeOf(only?.read_at);
  const deliveredTime = timeOf(only?.delivered_at);
  const groups = splitReceipts(data?.detail);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('info.title', { defaultValue: 'Message info' })}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('info.description', {
              defaultValue: 'Delivery and read status for this message.',
            })}
          </DialogDescription>
        </DialogHeader>

        {state === 'loading' && (
          <p className="py-4 text-sm text-muted-foreground" data-testid="info-loading">
            {t('info.loading', { defaultValue: 'Loading…' })}
          </p>
        )}

        {state === 'error' && (
          <p className="py-4 text-sm text-muted-foreground" data-testid="info-error">
            {t('info.error', { defaultValue: 'Could not load message info.' })}
          </p>
        )}

        {state === 'ready' && data && (
          <div className="divide-y divide-border" data-testid="message-info">
            {/* Newest first, the way the lifecycle is actually read. */}
            {direct && readTime && (
              <InfoRow
                testId="info-read"
                icon={<CheckCheck size={15} className="text-sky-500" aria-hidden="true" />}
                label={t('info.read', { defaultValue: 'Read' })}
                time={readTime}
              />
            )}
            {direct && deliveredTime && (
              <InfoRow
                testId="info-delivered"
                icon={<CheckCheck size={15} className="text-muted-foreground" aria-hidden="true" />}
                label={t('info.delivered', { defaultValue: 'Delivered' })}
                time={deliveredTime}
              />
            )}
            <InfoRow
              testId="info-sent"
              icon={<Check size={15} className="text-muted-foreground" aria-hidden="true" />}
              label={t('info.sent', { defaultValue: 'Sent' })}
              time={timeOf(sentAt) ?? ''}
            />

            {/* More than one recipient: counts, then who and when. */}
            {!direct && (
              <div className="space-y-3 pt-3" data-testid="info-counts">
                <PersonGroup
                  testId="info-read-group"
                  heading={t('info.read_by', {
                    count: data.read_count,
                    total: data.recipients,
                    defaultValue: 'Read by {{count}} of {{total}}',
                  })}
                  people={groups.read}
                  timeKey="read_at"
                  nameOf={nameOf}
                />
                <PersonGroup
                  testId="info-delivered-group"
                  heading={t('info.delivered_to', {
                    count: data.delivered_count,
                    total: data.recipients,
                    defaultValue: 'Delivered to {{count}} of {{total}}',
                  })}
                  people={groups.deliveredOnly}
                  timeKey="delivered_at"
                  nameOf={nameOf}
                />
              </div>
            )}

            {/* An empty breakdown must not read as "nobody has seen it". */}
            {data.detail_suppressed && (
              <p className="pt-3 text-xs text-muted-foreground" data-testid="info-suppressed">
                {t('info.too_many', {
                  defaultValue:
                    'This conversation is too large to list everyone — the counts above are exact.',
                })}
              </p>
            )}

            {/* Nothing yet is a real state, and saying so beats a bare Sent row
                that leaves the reader wondering whether it loaded. */}
            {direct && !readTime && !deliveredTime && (
              <p className="pt-3 text-xs text-muted-foreground" data-testid="info-pending">
                {t('info.not_delivered_yet', {
                  defaultValue: 'Not delivered yet.',
                })}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { Send as MessageInfoIcon };
